import { Divider, Form } from 'antd';

import { PageProps } from '@/types/PageProps';

import { DefaultRouteTabs } from '@/components/_common';
import ButtonsToGenerateReports from '@/components/Reports/ButtonsToGenerateReports';
import DirectSaleXFinancialForm from '@/components/Reports/MovementReports/DirectSaleXFinancialForm';
import { Helmet } from 'react-helmet';
import { useIntl } from 'react-intl';
import {
  QuestReportDirectSaleXFinancialFormValues,
  QuestReportDirectSaleXFinancialRequest,
} from '@/core/domain/questReport/Reports/DirectSaleXFinancial/DirectSaleXFinancial.types';
import useFileLoading, { FileType } from '@/hooks/useFileLoading';
import { useReportGenerate } from '@/hooks';
import { useDirectSaleXFinancialReport } from '@/core/domain/questReport/Reports/DirectSaleXFinancial/DirectSaleXFinancial.hook';
import { ReportCallback } from '@/hooks/useReportGenerate';
import { useDirectSaleXFinancialReportCsv } from '@/core/domain/questReport/CSV/DirectSaleXFinancial/DirectSaleXFinancial.hook';
import { DateTimeFormat } from '@/utils/moment';

function MovementDirectSaleXFinancialPage({ route }: PageProps) {
  const intl = useIntl();
  const [form] = Form.useForm<QuestReportDirectSaleXFinancialFormValues>();

  const { setFileLoading, setFileUnloading, fileLoading } = useFileLoading();
  const report = useReportGenerate();

  const generateReport = useDirectSaleXFinancialReport();
  const generateReportCsv = useDirectSaleXFinancialReportCsv();

  const handleGenerateReport = async (
    format: FileType,
    callback?: ReportCallback,
  ) => {
    setFileLoading(format);

    try {
      const {
        companiesIds,
        employeesIds,
        documentTypesIds,
        initialDate,
        finalDate,
        ...values
      } = await form.validateFields();

      const obj: QuestReportDirectSaleXFinancialRequest = {
        ...values,
        companiesIds: companiesIds?.toString(),
        employeesIds: employeesIds?.toString(),
        documentTypesIds: documentTypesIds?.toString(),
        startDate: initialDate.format(DateTimeFormat),
        endDate: finalDate.format(DateTimeFormat),
      };

      const generator = format === 'CSV' ? generateReportCsv : generateReport;
      const result = await generator.mutateAsync(obj);

      if (callback) {
        callback(result);
      }
    } catch {}

    setFileUnloading(format);
  };

  const handleView = () => {
    handleGenerateReport('REPORT_VIEW', (result) =>
      report.openFile({
        result,
        fileName: 'relatorio_vd_x_financeiro.pdf',
        fileType: 'application/pdf',
      }),
    );
  };

  const handlePdf = () => {
    handleGenerateReport('PDF', (result) =>
      report.downloadFile({
        result,
        fileName: 'relatorio_vd_x_financeiro.pdf',
      }),
    );
  };

  const handleCsv = () => {
    handleGenerateReport('CSV', (result) =>
      report.downloadFile({
        result,
        fileName: 'relatorio_vd_x_financeiro.csv',
      }),
    );
  };

  return (
    <DefaultRouteTabs route={route.parent} permissions={route.permissions}>
      <Helmet title={intl.formatMessage({ id: route.name })} />

      <DirectSaleXFinancialForm form={form} />

      <Divider />

      <ButtonsToGenerateReports
        viewReport={handleView}
        generatePdf={handlePdf}
        generateCsv={handleCsv}
        loading={fileLoading}
      />
    </DefaultRouteTabs>
  );
}

export default MovementDirectSaleXFinancialPage;
